import React, { useState, useEffect } from "react";
import { auth } from "../../services/firebase/Config";
import firebase from "firebase/app";
import { getUrlFile } from "../../services/firebase/storage";
import { guardaEmpresaUsuario } from "../../redux/empresa/empresaActions";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import NuevoLogo from "../../img/slider/nuevoLogo.png";
import { fetchUser } from "../../services/firebase/usuarios";
import { saveProspecto } from "../../services/firebase/prospectos";
import { useToasts } from "react-toast-notifications";
import { useLocalStorage } from "../../hooks/useLocalStorage";

import {
  WizardContainer,
  WizardLeftCol,
  WizardHeader,
} from "./registerStyles";
import { ProgressBar } from "./components/ProgressBar";
import { StepAuthGoogle } from "./components/StepAuthGoogle";
import { StepPersonalInfo } from "./components/StepPersonalInfo";
import { StepClinicName } from "./components/StepClinicName";
import { StepClinicType } from "./components/StepClinicType";
import { StepClinicRole } from "./components/StepClinicRole";
import { StepDiagnosisDoctores } from "./components/StepDiagnosisDoctores";
import { StepDiagnosisPacientes } from "./components/StepDiagnosisPacientes";
import { StepDiagnosisGestion } from "./components/StepDiagnosisGestion";
import { StepPlanResult } from "./components/StepPlanResult";
import { calculateRecommendedPlan } from "./utils/planCalculator";

export const Register = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { addToast } = useToasts();
  const [, setUsuario] = useLocalStorage("usuario", {});

  const [step, setStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [urlFile, setUrlFile] = useState("");
  const [checked, setChecked] = useState(false);

  const [formData, setFormData] = useState({
    nombres: "",
    email: "",
    uid: "",
    telefono: "",
    telefonoVerificado: false,
    fechaVerificacionTelefono: null,
    cop: "",
    razonSocial: "",
    tipoEstablecimiento: "independiente",
    rolUsuario: "ODONTOLOGO_DUENO",
    numDoctores: "1",
    volumenPacientes: "less_50",
    gestionActual: "papel",
  });

  const [selectedPlanId, setSelectedPlanId] = useState("BASIC");

  useEffect(() => {
    const getUrl = async () => {
      try {
        const url = await getUrlFile("sysdent/politica_de_privacidad.pdf");
        setUrlFile(url);
      } catch (err) {
        console.warn("No se pudo cargar la URL de la política:", err);
      }
    };
    getUrl();
  }, []);

  // Recalcular plan recomendado dinámicamente según diagnóstico
  const recommendedPlan = calculateRecommendedPlan({
    numDoctores: formData.numDoctores,
    volumenPacientes: formData.volumenPacientes,
    tipoEstablecimiento: formData.tipoEstablecimiento,
  });

  // Mantener sincronizado el plan seleccionado por defecto con el calculado
  useEffect(() => {
    if (recommendedPlan && recommendedPlan.id) {
      setSelectedPlanId(recommendedPlan.id);
    }
  }, [formData.numDoctores, formData.volumenPacientes, formData.tipoEstablecimiento]);

  const guardaAvance = (nextStep, pasoLabel, override = {}) => {
    setStep(nextStep);
    const email = formData.email;
    if (email) {
      saveProspecto(email, {
        ...formData,
        ...override,
        planRecomendado: recommendedPlan?.id || "BASIC",
        planSeleccionado: selectedPlanId || recommendedPlan?.id || "BASIC",
        pasoActual: nextStep,
        pasoLabel: pasoLabel,
      });
    }
  };

  const verificaUsuario = async (login) => {
    let usuario = await fetchUser(login.user.email);
    if (!usuario.id) {
      const nuevosDatos = {
        nombres: login.user.displayName || "",
        email: login.user.email || "",
        uid: login.user.uid,
      };
      setFormData((prev) => ({ ...prev, ...nuevosDatos }));
      setStep(1);
      saveProspecto(login.user.email, {
        email: login.user.email,
        nombres: login.user.displayName || "",
        uid: login.user.uid,
        pasoActual: 1,
        pasoLabel: "Datos Personales",
      });
    } else {
      addToast("Tu cuenta ya existe, ingresa desde Login", {
        appearance: "error",
      });
    }
  };

  const handleLoginWithGoogle = async () => {
    try {
      const provider = new firebase.auth.GoogleAuthProvider();

      const login = await auth.signInWithPopup(provider);
      const accessToken = login.credential ? login.credential.accessToken : null;

      verificaUsuario(login, accessToken);

      console.log("accessToken:", accessToken);

      await auth.getRedirectResult();
    } catch (err) {
      console.error("Error en el manejo de inicio de sesión con Google:", err);
      auth.signOut();
    }
  };

  const handleFinalSubmit = async () => {
    try {
      setIsLoading(true);

      if (!formData.razonSocial || formData.razonSocial.trim() === "") {
        throw new Error("Por favor ingrese el nombre del consultorio o clínica.");
      }
      if (!formData.telefono || formData.telefono.trim() === "") {
        throw new Error("Por favor ingrese su número de teléfono celular.");
      }

      const activePlanId = selectedPlanId || recommendedPlan.id || "BASIC";

      const usuario = {
        nombres: formData.nombres,
        email: formData.email,
        telefono: formData.telefono,
        telefonoVerificado: !!formData.telefonoVerificado,
        especialidad: "General",
        uid: formData.uid || "",
        cop: formData.cop || "",
        rol: formData.rolUsuario,
      };

      const empresa = {
        razonSocial: formData.razonSocial,
        nombreComercial: formData.razonSocial,
        tipoConsultorio: formData.tipoEstablecimiento,
        telefono: formData.telefono,
        telefonoVerificado: !!formData.telefonoVerificado,
        fechaVerificacionTelefono: formData.fechaVerificacionTelefono || new Date(),
        pais: "PE",
        googleCalendar: formData.email,
        tipoMembresia: activePlanId,
        plan: activePlanId.toLowerCase(),
        owner: usuario.email,
        diagnostico: {
          numDoctores: formData.numDoctores,
          volumenPacientes: formData.volumenPacientes,
          gestionActual: formData.gestionActual,
          rolUsuario: formData.rolUsuario,
          fechaDiagnostico: new Date(),
        },
      };

      const result = await dispatch(guardaEmpresaUsuario(empresa, usuario));

      await saveProspecto(formData.email, {
        ...formData,
        pasoActual: 9,
        pasoLabel: "Completado",
        estado: "completado",
        planRecomendado: recommendedPlan.id,
        planSeleccionado: activePlanId,
        empresaId: result?.empresa?.id || "",
        fechaCompletado: new Date(),
      });

      setUsuario(usuario);

      addToast("¡Consultorio registrado con éxito! Disfruta de tus 7 días de prueba gratis.", {
        appearance: "success",
      });

      // Redirige directamente al Dashboard principal sin wizard intermedio
      history.push("/");
    } catch (err) {
      console.error("Error al registrar consultorio:", err);
      addToast(err.message || "Error al completar el registro.", {
        appearance: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <WizardContainer>
      <WizardLeftCol>
        <div>
          <WizardHeader>
            <img src={NuevoLogo} alt="Sysdent Logo" className="logo" />
          </WizardHeader>

          <ProgressBar currentStep={step} totalSteps={9} />

          {step === 0 && (
            <StepAuthGoogle
              checked={checked}
              setChecked={setChecked}
              urlFile={urlFile}
              loginWithGoogle={handleLoginWithGoogle}
            />
          )}

          {step === 1 && (
            <StepPersonalInfo
              formData={formData}
              setFormData={setFormData}
              onNext={() => guardaAvance(2, "Tu Consultorio")}
              onBack={() => setStep(0)}
            />
          )}

          {step === 2 && (
            <StepClinicName
              formData={formData}
              setFormData={setFormData}
              onNext={() => guardaAvance(3, "Tipo Clinica")}
              onBack={() => setStep(1)}
            />
          )}

          {step === 3 && (
            <StepClinicType
              formData={formData}
              setFormData={setFormData}
              onNext={(field, value) => guardaAvance(4, "Tu Rol", { [field]: value })}
              onBack={() => setStep(2)}
            />
          )}

          {step === 4 && (
            <StepClinicRole
              formData={formData}
              setFormData={setFormData}
              onNext={(field, value) => guardaAvance(5, "Tu Equipo", { [field]: value })}
              onBack={() => setStep(3)}
            />
          )}

          {step === 5 && (
            <StepDiagnosisDoctores
              formData={formData}
              setFormData={setFormData}
              onNext={(field, value) => guardaAvance(6, "Pacientes", { [field]: value })}
              onBack={() => setStep(4)}
            />
          )}

          {step === 6 && (
            <StepDiagnosisPacientes
              formData={formData}
              setFormData={setFormData}
              onNext={(field, value) => guardaAvance(7, "Gestion", { [field]: value })}
              onBack={() => setStep(5)}
            />
          )}

          {step === 7 && (
            <StepDiagnosisGestion
              formData={formData}
              setFormData={setFormData}
              onNext={(field, value) => guardaAvance(8, "Plan Ideal", { [field]: value })}
              onBack={() => setStep(6)}
            />
          )}

          {step === 8 && (
            <StepPlanResult
              recommendedPlan={recommendedPlan}
              selectedPlanId={selectedPlanId}
              setSelectedPlanId={setSelectedPlanId}
              onSubmit={handleFinalSubmit}
              onBack={() => setStep(7)}
              isSubmitting={isLoading}
            />
          )}
        </div>
      </WizardLeftCol>
    </WizardContainer>
  );
};
